import { Box } from "@mui/material";
import PaginationControls from "components/fileStorage/PaginationControls";
import IntegrationsBetaTable from "components/integrations-beta/IntegrationsBetaTable";
import IntegrationsFilterBar, {
  type IntegrationsFilters,
} from "components/integrations-beta/IntegrationsFilterBar";
import ManagementPageShell from "components/management/ManagementPageShell";
import { MANAGEMENT_TABLE_AREA_SX } from "components/management/managementTheme";
import {
  IntegrationMonitorSortField,
  useGetIntegrationMonitorListQuery,
} from "features/integrationsBeta/integrationsBeta";
import { useTimezoneDisplayMode } from "features/integrationsBeta/timezoneDisplay";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

const DEFAULT_PAGE_SIZE = 50;

export default function IntegrationsBetaPage() {
  const navigate = useNavigate();
  const { isSuperuser } = usePlatformRole();
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<
    IntegrationMonitorSortField | undefined
  >();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [filters, setFilters] = useState<IntegrationsFilters>({});
  const [timezoneMode, setTimezoneMode] = useTimezoneDisplayMode();

  const queryArgs = useMemo(
    () => ({
      page,
      page_size: pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...filters,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    }),
    [page, pageSize, search, filters, sortBy, sortOrder],
  );

  const { data, isLoading, isFetching, error } =
    useGetIntegrationMonitorListQuery(queryArgs, {
      refetchOnMountOrArgChange: true,
    });

  const integrations = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handleSortChange = (field: IntegrationMonitorSortField) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const handleSearch = (term: string | null) => {
    setSearch(term ?? "");
    setPage(1);
  };

  const handleFiltersChange = (next: IntegrationsFilters) => {
    setFilters(next);
    setPage(1);
  };

  return (
    <Layout
      title="Integrations Platform"
      content="Integrations Platform"
      noScroll
    >
      <ManagementPageShell title="Integrations Platform">
        <Box mb={1.5} sx={{ flexShrink: 0 }}>
          <IntegrationsFilterBar
            filters={filters}
            onChange={handleFiltersChange}
            search={search}
            onSearch={handleSearch}
            timezoneMode={timezoneMode}
            onTimezoneModeChange={setTimezoneMode}
            showRegister={isSuperuser}
            onRegister={() => navigate("/integrations-beta/register")}
          />
        </Box>

        <Box sx={{ ...MANAGEMENT_TABLE_AREA_SX, flexDirection: "column" }}>
          <IntegrationsBetaTable
            integrations={integrations}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            timezoneMode={timezoneMode}
            pagination={
              totalItems > 0 ? (
                <Box
                  sx={{
                    position: "relative",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <PaginationControls
                    currentLimit={pageSize}
                    onLimitChange={(limit) => {
                      setPageSize(limit);
                      setPage(1);
                    }}
                    totalItems={totalItems}
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    showLimitSelector={true}
                    showPageInfo={true}
                    disabled={isFetching}
                    size="small"
                    pageTokens={[]}
                    hasNextPage={page < totalPages}
                    borderStyle={{ borderTop: "none", borderColor: "none" }}
                    buttonStyle={{
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                </Box>
              ) : null
            }
          />
        </Box>
      </ManagementPageShell>
    </Layout>
  );
}
